import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  product,
  breadcrumbs,
  includeOrganizationSchema = true,
  includeProductSchema = false 
}) => {
  const siteName = 'ASMAR SHOP';
  const defaultTitle = 'ASMAR SHOP - Vapes y Perfumes Árabes en Paraguay | Tienda Online';
  const defaultDescription = 'ASMAR SHOP - Tu tienda de confianza en Paraguay. Vapes, vaporizadores, pods desechables, e-liquids, perfumes árabes importados, oud, bakhoor, attar y accesorios. Envíos a todo Paraguay y Brasil. ✓ Productos originales ✓ Mejores precios ✓ Atención por WhatsApp';
  const defaultKeywords = 'vapes paraguay, perfumes árabes paraguay, vape shop asunción, vaporizadores paraguay, pod desechable paraguay, e-liquid paraguay, juice vape, esencias árabes, oud perfume, bakhoor, attar, musk árabe, perfumería árabe paraguay, cigarrillos electrónicos, tienda vape, asmar shop, vape al por mayor, perfumes importados paraguay, vape pods, coils vape, resistencias vape, accesorios vape paraguay, comprar vape paraguay, perfumes orientales, esencias orientales, tienda online paraguay';
  const defaultImage = 'https://asmarshop.com/logo.jpg';
  const defaultUrl = 'https://asmarshop.com';

  const seo = {
    title: title ? (title.includes(siteName) ? title : `${title} | ${siteName}`) : defaultTitle,
    description: description || defaultDescription,
    keywords: keywords || defaultKeywords,
    image: image || defaultImage,
    url: url || defaultUrl,
  };

  // Store Schema for local business SEO
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Store",
    "@id": `${defaultUrl}/#organization`,
    "name": "ASMAR SHOP",
    "alternateName": "Asmar Shop Paraguay",
    "description": "Tienda de vapes, vaporizadores, pods, e-liquids, perfumes árabes importados, oud, bakhoor y accesorios en Paraguay",
    "image": defaultImage,
    "logo": defaultImage,
    "url": defaultUrl,
    "telephone": "+595981000000",
    "email": "info@asmarshop.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Asunción",
      "addressRegion": "Central",
      "addressCountry": "PY"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -25.2637,
      "longitude": -57.5759
    },
    "priceRange": "$$",
    "currenciesAccepted": "USD, PYG, BRL",
    "paymentAccepted": "Efectivo, Transferencia, Tarjeta",
    "areaServed": [
      { "@type": "Country", "name": "Paraguay" },
      { "@type": "Country", "name": "Brasil" }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Catálogo ASMAR SHOP",
      "itemListElement": [
        {
          "@type": "OfferCatalog",
          "name": "Vapes y Vaporizadores",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Pods Desechables" } },
            { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Vaporizadores" } },
            { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "E-Liquids / Juice" } },
            { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Coils y Resistencias" } }
          ]
        },
        {
          "@type": "OfferCatalog",
          "name": "Perfumes Árabes",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Perfumes Oud" } },
            { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Bakhoor" } },
            { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Attar" } },
            { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Musk Árabe" } }
          ]
        }
      ]
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "08:00",
        "closes": "20:00"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/asmarshop",
      "https://www.instagram.com/asmarshop",
      "https://wa.me/595981000000"
    ]
  };

  // Product Schema — rich snippets for product pages
  const productSchema = product ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.title,
    "image": product.image,
    "description": product.description,
    "sku": product.sku || product.slug,
    "brand": {
      "@type": "Brand",
      "name": product.brand || "ASMAR SHOP"
    },
    "category": product.category || "Vapes y Perfumes",
    "offers": {
      "@type": "Offer",
      "url": seo.url,
      "priceCurrency": "USD",
      "price": product.price,
      "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      "availability": product.stock_qty > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "ASMAR SHOP",
        "url": defaultUrl
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "PY"
        }
      }
    },
    ...(product.rating ? {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": product.rating,
        "bestRating": 5,
        "worstRating": 1,
        "reviewCount": product.review_count || 1
      }
    } : {})
  } : null;

  // Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs?.length ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang="es-PY" />
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <link rel="canonical" href={seo.url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="es_PY" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />

      {/* Additional SEO Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content="ASMAR SHOP" />
      <meta name="geo.region" content="PY" />
      <meta name="geo.placename" content="Asunción, Paraguay" />
      <meta name="geo.position" content="-25.2637;-57.5759" />
      <meta name="language" content="es" />
      
      {/* Schema.org structured data */}
      {includeOrganizationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}
      
      {includeProductSchema && productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}

      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
